import * as React from "react";
import { graphql } from "gatsby";
import { Trans, useTranslation } from "gatsby-plugin-react-i18next";
import PageLayout from "../components/page-layout/PageLayout";
import SEO from "../components/SEO";
// @ts-ignore
import rei from "../assets/images/faq/rei.png";
import TextHero from "../components/hero/TextHero";
import Button from "../components/form/Button";
import { useClickOutside } from "../hooks/useClickOutside";
import { twMerge } from "tailwind-merge";
import { Faq } from "../components/FAQ";
import { Theme } from "../hooks/useTheme";
import { useConfig } from "../hooks/useConfig";

const mockStream = async (
  response: string,
  setter: React.Dispatch<React.SetStateAction<string>>,
) => {
  setter("");
  for (let i = 0; i < response.length; i += 4) {
    const chunk = response.slice(i, i + 4);
    setter((prev) => (prev ? prev + chunk : chunk));
    await new Promise((r) => setTimeout(r, 10));
  }
};

const fnv1a32 = (str: string) => {
  let h = 0x811c9dc5 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
};

const useReiAnswerer = () => {
  const config = useConfig();
  const { t } = useTranslation();
  const [pristine, setPristine] = React.useState(true);
  const [answerCounter, setAnswerCounter] = React.useState(0);
  const [generating, setGenerating] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [res, setRes] = React.useState<string>(t("faq:askRei.reiPresentation"));

  React.useEffect(() => {
    setAnswerCounter(parseInt(localStorage.getItem("reiAnswers") || "0"));
  }, []);

  React.useEffect(() => {
    localStorage.setItem("reiAnswers", answerCounter.toString());
  }, [answerCounter]);

  const ask = async (query: string) => {
    if (!query.trim()) return;

    setAnswerCounter((c) => c + 1);
    setPristine(false);

    if (generating) return;

    if (answerCounter >= config.faq.maxAnswers) {
      await mockStream(t("faq:askRei.reiCta"), setRes);
      return;
    }

    setGenerating(true);
    setLoading(true);
    await mockStream(t("faq:askRei.loading"), setRes);

    try {
      const body = JSON.stringify({
        query,
        faq: (
          t("faq:sections", { returnObjects: true }) as { questions: [] }[]
        ).flatMap((s) => s.questions),
      });

      const res = await fetch(config.faq.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-rei-check": fnv1a32(body),
          Accept: "text/event-stream",
        },
        body: body,
      });

      if (!res.ok || !res.body) {
        setGenerating(false);
        setLoading(false);
        await mockStream(t("faq:askRei.error"), setRes);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");

      setLoading(false);
      setRes("");
      while (true) {
        const { value, done } = await reader.read();
        if (value) {
          const chunkValue = decoder.decode(value);
          setRes((prev) => (prev ? prev + chunkValue : chunkValue));
        }
        if (done) break;
      }

      setGenerating(false);
    } catch (e) {
      setGenerating(false);
      setLoading(false);
      await mockStream(t("faq:askRei.error"), setRes);
    }
  };

  return { res, generating, ask, loading, pristine };
};

const Searchbox = () => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [focus, setFocus] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const { t } = useTranslation();
  const { ask, res, loading, generating, pristine } = useReiAnswerer();
  const showRei = focus || !pristine;

  useClickOutside(containerRef, () => {
    if (pristine) setQuery("");
    setFocus(false);
  });

  const handleKeyPress = React.useCallback(
    (event) => {
      if (event.keyCode === 75 && event.metaKey) {
        inputRef.current?.focus();
        setFocus(true);
      }
    },
    [inputRef],
  );

  React.useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [handleKeyPress]);

  return (
    <div className="max-w-5xl mx-auto w-full mt-8" ref={containerRef}>
      <div
        className={twMerge(
          "flex justify-end items-end mb-4",
          showRei ? "min-h-[24rem] sm:min-h-[15rem]" : "h-0 min-h-0",
        )}
        style={{
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
          transitionProperty: "height, min-height",
          transitionDelay: showRei ? "0ms" : "200ms",
          transitionDuration: "200ms",
        }}
      >
        <div
          className={twMerge(
            "h-min bg-white border border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 rounded-2xl rounded-br-none p-6 text-gray-600 text-left -translate-x-12 opacity-0 lg:mb-8",
            showRei && "opacity-100 translate-x-0",
          )}
          style={{
            transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
            transitionProperty: "opacity, transform, height",
            transitionDelay: showRei ? "200ms" : "0ms",
            transitionDuration: "200ms",
          }}
        >
          <p className={twMerge(loading && "animate-pulse")}>
            <Trans
              i18nKey="faq:askRei.response"
              values={{ response: res }}
              components={{
                cf: (
                  <a
                    href="#contact-form"
                    className="underline text-indigo-500 dark:text-indigo-400"
                  />
                ),
              }}
            />
          </p>
        </div>
        <img
          src={rei}
          className={twMerge(
            "shrink-0 w-16 md:w-20 xl:w-28 ml-4 xl:ml-8 mt-auto opacity-0 translate-x-12",
            showRei && "opacity-100 translate-x-0",
          )}
          style={{
            transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
            transitionProperty: "opacity, transform",
            transitionDelay: showRei ? "200ms" : "0ms",
            transitionDuration: "200ms",
          }}
        />
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          ask(query);
        }}
        className="relative h-16 flex items-center"
      >
        <input
          className="transition-all block w-full h-full pl-4 pr-20 rounded-2xl border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-500 focus:border-indigo-500 focus:ring-indigo-500"
          type="text"
          placeholder={t("faq:askRei.placeholder")}
          autoComplete="off"
          autoCorrect="off"
          onFocus={() => setFocus(true)}
          disabled={generating}
          onChange={(e) => setQuery(e.target.value)}
          value={query}
          ref={inputRef}
        />
        <div className="absolute inset-y-0 right-0 flex py-2 pr-2">
          {!focus ? (
            <kbd className="inline-flex items-center rounded-xl border border-gray-200 dark:border-gray-700 px-4 font-sans font-medium text-gray-400">
              ⌘K
            </kbd>
          ) : (
            <Button
              type="submit"
              className="w-14 min-w-24 rounded-xl"
              disabled={generating}
              text={"Ask"}
            />
          )}
        </div>
      </form>
      <p
        className={twMerge(
          "mt-4 italic text-xs text-gray-400 text-center mx-auto max-w-xl opacity-0 translate-y-6",
          showRei && "opacity-100 translate-y-0",
        )}
        style={{
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
          transitionProperty: "opacity, transform",
          transitionDelay: showRei ? "200ms" : "0ms",
          transitionDuration: "200ms",
        }}
      >
        {t("faq:askRei.disclaimer")}
      </p>
    </div>
  );
};

const FAQPage = () => {
  const { t } = useTranslation();

  const sections: {
    title: string;
    description: string;
    theme?: Theme;
    questions: {
      question: string;
      answer: string;
    }[];
  }[] = t("faq:sections", { returnObjects: true });

  return (
    <PageLayout headerBg="gray-100">
      <SEO
        titleI18nKey={"faq:meta.title"}
        descriptionI18nKey={"faq:meta.description"}
        datePublished={new Date("2021-03-01T08:00:00Z")}
      />
      <TextHero
        tag={t("faq:hero.tag")}
        title={
          <Trans
            i18nKey="faq:hero.title"
            components={{ ol: <span className="text-ol-gradient" /> }}
          />
        }
        description={t("faq:hero.description")}
        cta={<Searchbox />}
      />
      {sections.map((section, i) => (
        <Faq
          key={`faq-section-${i}`}
          theme={section.theme}
          tag={"faq"}
          bgTag={false}
          title={section.title}
          description={section.description}
          faqs={section.questions}
          firstOpen={false}
          chunked
        />
      ))}
    </PageLayout>
  );
};

export default FAQPage;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
